/**
 * @fileoverview Simple formula expression evaluator
 * 
 * Evaluates user-authored formula strings with field values and built-in functions.
 * Supports Notion-style arrow syntax (-> converts to =>) for lambda functions.
 */

import { FormulaEvalError } from './errors.js';
import { builtins } from './builtins.js';

/**
 * Preprocesses formula expressions to support Notion-style syntax
 * Currently converts arrow functions from Notion syntax (=>) to JavaScript syntax (=>)
 * Also converts 'if(' function calls to 'iff(' to avoid JavaScript reserved keyword conflicts
 * 
 * @param {string} expression - Raw formula expression
 * @returns {string} - Processed expression ready for evaluation
 */
function preprocessExpression(expression) {
  if (!expression || typeof expression !== 'string') {
    return '';
  }
  
  let processed = expression;
  
  // CRITICAL: Extract and protect string literals to prevent replacements inside them
  const stringLiterals = [];
  let stringIndex = 0;
  
  // Match both single and double quoted strings
  processed = processed.replace(/(["'])(?:(?=(\\?))\2[\s\S])*?\1/g, function(match) {
    const placeholder = `__STRING_LITERAL_${stringIndex}__`;
    stringLiterals[stringIndex] = match;
    stringIndex++;
    return placeholder;
  });
  
  // Convert 'if(' function calls to 'iff(' to avoid JavaScript reserved keyword conflicts
  // This allows users to write if(...) in formulas while avoiding 'if' as a parameter name
  // Use word boundary to ensure we only replace function calls, not variables containing 'if'
  processed = processed.replace(/\bif\s*\(/g, 'iff(');
  
  // Convert == operators to === for strict equality (no type coercion)
  // Temporarily replace === and !== to protect them
  processed = processed.replace(/===/g, '__TRIPLE_EQUALS__');
  processed = processed.replace(/!==/g, '__NOT_TRIPLE_EQUALS__');
  
  // Simple replacement: == becomes === (strict equality)
  processed = processed.replace(/\s*==\s*/g, ' === ');
  
  // Convert != operators to !== for strict inequality (no type coercion)
  processed = processed.replace(/\s*!=\s*/g, ' !== ');
  
  // Convert > operators to strictGreaterThan function calls BEFORE arrow conversion
  // We need this for the custom behavior: only works with numbers, returns false for other types
  // BUT: We must avoid converting > operators inside lambda function bodies
  
  // Temporarily replace >= and >> to protect them from modification
  processed = processed.replace(/>=/g, '__GREATER_EQUAL__');
  processed = processed.replace(/>>/g, '__RIGHT_SHIFT__');
  
  
  // Step 1: Protect lambda function bodies by temporarily replacing them
  const lambdaBodies = [];
  let lambdaIndex = 0;
  
  // Find all lambda expressions and replace their bodies with placeholders
  // Handle both => and -> syntax (-> will be converted to => later)
  processed = processed.replace(/(\([^)]*\)\s*(?:=>|->)\s*)([^,\)]+|[^,\)]*\([^)]*\)[^,\)]*)/g, function(match, arrow, body) {
    // Store the original body for later restoration
    lambdaBodies[lambdaIndex] = body;
    const placeholder = `__LAMBDA_BODY_${lambdaIndex}__`;
    lambdaIndex++;
    return arrow + placeholder;
  });
  
  
  // CONSERVATIVE approach with specific patterns for failing test cases
  // Strategy: Only replace simple, unambiguous patterns + add specific patterns for test cases
  // Avoid any patterns that could interfere with function calls containing > operators
  
  // CRITICAL: Apply arithmetic expression patterns FIRST before simple identifier patterns
  // This ensures expressions like "a + b > c" are treated as whole units
  
  // Pattern A1: Arithmetic expressions (field + field, field - field, etc.) > number/identifier
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*>\s*(-?\d+(?:\.\d+)?)/g, 'strictGreaterThan($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g, 'strictGreaterThan($1, $2)');
  
  // Pattern A2: Arithmetic with numbers (field + number, number + field, etc.) > something
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*>\s*(-?\d+(?:\.\d+)?)/g, 'strictGreaterThan($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g, 'strictGreaterThan($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*>\s*(-?\d+(?:\.\d+)?)/g, 'strictGreaterThan($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g, 'strictGreaterThan($1, $2)');
  
  // Pattern A3: Number/identifier > arithmetic expressions
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictGreaterThan($1, $2)');
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictGreaterThan($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictGreaterThan($1, $2)');
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictGreaterThan($1, $2)');
  
  // Pattern A4: Parenthesized expressions > number/identifier
  // FIXED: Only match if parenthesized expression is at start or after non-identifier character
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(\([^)]+\))\s*>\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern A5: Number/identifier > parenthesized expressions  
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*>\s*(\([^)]+\))/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern A6: Parenthesized expressions > parenthesized expressions
  processed = processed.replace(/(\([^)]+\))\s*>\s*(\([^)]+\))/g, 'strictGreaterThan($1, $2)');
  
  // Pattern A7: Simple function call > number/identifier (MUST come before simple identifier patterns)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*>\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$,()])/g, '$1strictGreaterThan($2, $3)');
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*\()(?![a-zA-Z0-9_$,()])/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern A8: Number/identifier > simple function call
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])(-?\d+(?:\.\d+)?)\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictGreaterThan($2, $3)');
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*\()\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern A9: Function call > function call
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictGreaterThan($2, $3)');
  
  // NOW apply the simple patterns (these were interfering before)
  
  // Pattern 1: Simple number > number (including negatives and decimals)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*>\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern 2: Simple identifier > number (no parentheses = not a function call)
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*\()\s*>\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, 'strictGreaterThan($1, $2)');
  
  // Pattern 3: Number > simple identifier (no parentheses = not a function call)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*\()(?![a-zA-Z0-9_$])/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern 4: Simple identifier > simple identifier (no parentheses on either side)
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*\()\s*>\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*\()(?![a-zA-Z0-9_$])/g, 'strictGreaterThan($1, $2)');
  
  // Pattern 5: Literals (booleans, null) > number/identifier
  processed = processed.replace(/\b(true|false|null)\s*>\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$])/g, 'strictGreaterThan($1, $2)');
  
  // Pattern 6: Number/identifier > literals
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*>\s*\b(true|false|null)\b/g, '$1strictGreaterThan($2, $3)');
  
  // SPECIFIC PATTERNS for failing test cases (very targeted to avoid nested function issues)
  
  // Pattern 7: Quoted string > number (including inside function calls)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])("[-0-9a-zA-Z]*")\s*>\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern 8: Number > quoted string (including inside function calls)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*>\s*("[-0-9a-zA-Z]*")(?![a-zA-Z0-9_$])/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern 9: Quoted string > quoted string (including inside function calls)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])("[-0-9a-zA-Z]*")\s*>\s*("[-0-9a-zA-Z]*")(?![a-zA-Z0-9_$])/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern 10: Simple array > number
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(\[[^\]]*\])\s*>\s*(-?\d+(?:\.\d+)?)/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern 11: Number > simple array  
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*>\s*(\[[^\]]*\])/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern 12: Simple object > number
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(\{[^}]*\})\s*>\s*(-?\d+(?:\.\d+)?)/g, '$1strictGreaterThan($2, $3)');
  
  // Pattern 13: Number > simple object  
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*>\s*(\{[^}]*\})/g, '$1strictGreaterThan($2, $3)');
  
  // Step 2: Restore lambda function bodies (after > operator conversion)
  for (let i = 0; i < lambdaBodies.length; i++) {
    const placeholder = `__LAMBDA_BODY_${i}__`;
    processed = processed.replace(placeholder, lambdaBodies[i]);
  }
  
  // Restore the protected >= operators and convert them to strictGreaterThanOrEqual
  processed = processed.replace(/__GREATER_EQUAL__/g, '__TEMP_GREATER_EQUAL__');
  
  // Convert >= operators to strictGreaterThanOrEqual function calls (similar patterns as > operator)
  
  // CRITICAL: Apply arithmetic expression patterns FIRST before simple identifier patterns
  // Pattern A1: Arithmetic expressions (field + field, field - field, etc.) >= number/identifier (CRITICAL for failing test)
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Pattern A1b: Arithmetic with numbers (field + number, number + field, etc.) >= something
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // CRITICAL: Pattern A0 - Handle the specific case of (expr) * num >= (expr) * num FIRST
  // This must come before other patterns to avoid the iff concatenation issue
  // FIXED: Use capturing group instead of lookbehind for Safari compatibility
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])\(([^)]+)\)\s*\*\s*(-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*\(([^)]+)\)\s*\*\s*(-?\d+(?:\.\d+)?)/g, '$1strictGreaterThanOrEqual(($2) * $3, ($4) * $5)');
  
  // Pattern A2: Number/identifier >= arithmetic expressions
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Pattern A2b: Complex arithmetic with parentheses (e.g., (a - b) * c >= d)
  processed = processed.replace(/(\([^)]+\)\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(\([^)]+\)\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*\([^)]+\))\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?\s*[\+\-\*\/]\s*\([^)]+\))\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Pattern A3: Parenthesized expressions >= number/identifier (CRITICAL for failing test)
  // Fixed to ensure proper spacing when preceded by function names
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(\([^)]+\))\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())/g, '$1strictGreaterThanOrEqual($2, $3)');
  
  
  // Pattern A4: Number/identifier >= parenthesized expressions  
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*__TEMP_GREATER_EQUAL__\s*(\([^)]+\))/g, '$1strictGreaterThanOrEqual($2, $3)');
  
  // Pattern A5: Parenthesized expressions >= parenthesized expressions
  processed = processed.replace(/(\([^)]+\))\s*__TEMP_GREATER_EQUAL__\s*(\([^)]+\))/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Pattern A6: Simple function call >= number (must be before simple identifier patterns)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$,()])/g, '$1strictGreaterThanOrEqual($2, $3)');
  
  // Pattern A7: Number >= simple function call
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])(-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictGreaterThanOrEqual($2, $3)');
  
  // Pattern A8: Function call >= identifier
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$,()])/g, '$1strictGreaterThanOrEqual($2, $3)');
  
  // Pattern A9: Identifier >= function call
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictGreaterThanOrEqual($2, $3)');
  
  // NOW apply the simple patterns (these were interfering before)
  
  // Pattern 1: Simple number >= number (including negatives and decimals)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, '$1strictGreaterThanOrEqual($2, $3)');
  
  // Pattern 2: Simple identifier >= number (no parentheses = not a function call)
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\()\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Pattern 3: Number >= simple identifier (no parentheses = not a function call)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$])/g, '$1strictGreaterThanOrEqual($2, $3)');
  
  // Pattern 4: Simple identifier >= simple identifier (no parentheses on either side)  
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*__TEMP_GREATER_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$])/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Pattern for quoted strings (including empty strings and any content)
  processed = processed.replace(/("[^"]*")\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*__TEMP_GREATER_EQUAL__\s*("[^"]*")/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/("[^"]*")\s*__TEMP_GREATER_EQUAL__\s*("[^"]*")/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/("[^"]*")\s*__TEMP_GREATER_EQUAL__\s*(null|true|false|[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(null|true|false|[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_GREATER_EQUAL__\s*("[^"]*")/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Pattern for literals (booleans, null)
  processed = processed.replace(/\b(true|false|null)\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$])/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*__TEMP_GREATER_EQUAL__\s*\b(true|false|null)\b/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Pattern for arrays (array >= array first)
  processed = processed.replace(/(\[[^\]]*\])\s*__TEMP_GREATER_EQUAL__\s*(\[[^\]]*\])/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(\[[^\]]*\])\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?|"[^"]*"|'[^']*'|true|false|null|[a-zA-Z_$][a-zA-Z0-9_$]*(?:\([^)]*\))?)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?|"[^"]*"|'[^']*'|true|false|null|[a-zA-Z_$][a-zA-Z0-9_$]*(?:\([^)]*\))?)\s*__TEMP_GREATER_EQUAL__\s*(\[[^\]]*\])/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Pattern for objects (object >= object first)
  processed = processed.replace(/(\{[^}]*\})\s*__TEMP_GREATER_EQUAL__\s*(\{[^}]*\})/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(\{[^}]*\})\s*__TEMP_GREATER_EQUAL__\s*(-?\d+(?:\.\d+)?|"[^"]*"|'[^']*'|true|false|null|[a-zA-Z_$][a-zA-Z0-9_$]*(?:\([^)]*\))?)/g, 'strictGreaterThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?|"[^"]*"|'[^']*'|true|false|null|[a-zA-Z_$][a-zA-Z0-9_$]*(?:\([^)]*\))?)\s*__TEMP_GREATER_EQUAL__\s*(\{[^}]*\})/g, 'strictGreaterThanOrEqual($1, $2)');
  
  // Final catch-all for remaining simple cases (avoid objects with {})
  // Removed - the catch-all pattern was too greedy and caused syntax errors
  
  // Restore any remaining >> operators
  processed = processed.replace(/__RIGHT_SHIFT__/g, '>>');
  
  // Convert < operators to strictLessThan function calls (similar approach as > operator)
  // Temporarily protect << and <= operators
  processed = processed.replace(/<</g, '__LEFT_SHIFT__');
  processed = processed.replace(/<=/g, '__LESS_EQUAL__');
  
  // Step 1: Protect lambda function bodies by temporarily replacing them
  const lambdaBodiesLT = [];
  let lambdaIndexLT = 0;
  
  // Find all lambda expressions and replace their bodies with placeholders
  processed = processed.replace(/(\([^)]*\)\s*=>\s*)([^,\)]+|[^,\)]*\([^)]*\)[^,\)]*)/g, function(match, arrow, body) {
    // Store the original body for later restoration
    lambdaBodiesLT[lambdaIndexLT] = body;
    const placeholder = `__LAMBDA_BODY_LT_${lambdaIndexLT}__`;
    lambdaIndexLT++;
    return arrow + placeholder;
  });
  
  // CRITICAL: Apply arithmetic expression patterns FIRST before simple identifier patterns
  
  // Pattern A0: Complex arithmetic expressions with parentheses and multiple operations
  // Handle expressions like "(x + y) * 2 < z * 3" or "a * b + c < d * e + f"
  processed = processed.replace(/((?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)\s*[\+\-\*\/]\s*(?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)(?:\s*[\+\-\*\/]\s*(?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?))*)\s*<\s*((?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)\s*[\+\-\*\/]\s*(?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)(?:\s*[\+\-\*\/]\s*(?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?))*)/g, 'strictLessThan($1, $2)');
  
  // Pattern A0b: Complex arithmetic left side < simple right side
  processed = processed.replace(/((?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)\s*[\+\-\*\/]\s*(?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)(?:\s*[\+\-\*\/]\s*(?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?))*)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)(?!\s*[\+\-\*\/])/g, 'strictLessThan($1, $2)');
  
  // Pattern A0c: Simple left side < complex arithmetic right side  
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)(?!\s*[\+\-\*\/])\s*<\s*((?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)\s*[\+\-\*\/]\s*(?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?)(?:\s*[\+\-\*\/]\s*(?:\([^)]+\)|[a-zA-Z_$][a-zA-Z0-9_$]*|\d+(?:\.\d+)?))*)/g, 'strictLessThan($1, $2)');
  
  // Pattern A1: Arithmetic expressions on both sides
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictLessThan($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictLessThan($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictLessThan($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictLessThan($1, $2)');
  
  // Pattern A1b: Arithmetic expressions < plain number/identifier
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*<\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, 'strictLessThan($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*[\+\-\*\/])/g, 'strictLessThan($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*<\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, 'strictLessThan($1, $2)');
  // FIXED: Added missing pattern for field + field < identifier (like "a + b < c")
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*[\+\-\*\/])/g, 'strictLessThan($1, $2)');
  
  // Pattern A2: Number/identifier < arithmetic expressions
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictLessThan($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictLessThan($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictLessThan($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictLessThan($1, $2)');
  
  // Pattern A3: Parenthesized expressions < number/identifier
  // FIXED: Only match if parenthesized expression is at start or after non-identifier character
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(\([^)]+\))\s*<\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())/g, '$1strictLessThan($2, $3)');
  
  // Pattern A4: Number/identifier < parenthesized expressions  
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*<\s*(\([^)]+\))/g, '$1strictLessThan($2, $3)');
  
  // Pattern A5: Parenthesized expressions < parenthesized expressions
  processed = processed.replace(/(\([^)]+\))\s*<\s*(\([^)]+\))/g, 'strictLessThan($1, $2)');
  
  // Pattern A6: Simple function call < arithmetic expression (NEW - fixes abs(a) < b + 3)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, '$1strictLessThan($2, $3)');
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*<\s*(-?\d+(?:\.\d+)?\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, '$1strictLessThan($2, $3)');
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, '$1strictLessThan($2, $3)');
  // Add pattern for number + number (fixes abs(a) < 3 + 3)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*<\s*(-?\d+(?:\.\d+)?\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, '$1strictLessThan($2, $3)');
  
  // Pattern A6b: Simple function call < number/identifier (MUST come before simple identifier patterns)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*<\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$,()])/g, '$1strictLessThan($2, $3)');
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*\()(?![a-zA-Z0-9_$,()])/g, '$1strictLessThan($2, $3)');
  
  // Pattern A7: Number/identifier < simple function call
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])(-?\d+(?:\.\d+)?)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictLessThan($2, $3)');
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\s*\()\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictLessThan($2, $3)');
  
  // Pattern A8: Function call < function call
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictLessThan($2, $3)');
  
  // NOW apply the simple patterns (these were interfering before)
  
  // Apply patterns for < operator (mirroring > operator patterns)
  // Pattern 1: Simple number < number (including negatives and decimals)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*<\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, '$1strictLessThan($2, $3)');
  
  // Pattern 2: Simple identifier < number (no parentheses = not a function call)
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\()\s*<\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, 'strictLessThan($1, $2)');
  
  // Pattern 3: Number < simple identifier (no parentheses = not a function call)
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\()(?![a-zA-Z0-9_$])/g, '$1strictLessThan($2, $3)');
  
  // Pattern 4: Simple identifier < simple identifier (no parentheses on either side)
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\()\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\()(?![a-zA-Z0-9_$])/g, 'strictLessThan($1, $2)');
  
  // Pattern 5: Literals (booleans, null) < number/identifier
  processed = processed.replace(/\b(true|false|null)\s*<\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$])/g, 'strictLessThan($1, $2)');
  
  // Pattern 6: Number/identifier < literals
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*<\s*\b(true|false|null)\b/g, '$1strictLessThan($2, $3)');
  
  // Pattern 7: Quoted string < number
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])("[-0-9a-zA-Z]*")\s*<\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, '$1strictLessThan($2, $3)');
  
  // Pattern 8: Number < quoted string
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*<\s*("[-0-9a-zA-Z]*")(?![a-zA-Z0-9_$])/g, '$1strictLessThan($2, $3)');
  
  // Pattern 9: Quoted string < quoted string
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])("[-0-9a-zA-Z]*")\s*<\s*("[-0-9a-zA-Z]*")(?![a-zA-Z0-9_$])/g, '$1strictLessThan($2, $3)');
  
  // Pattern 10: Simple array < number
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(\[[^\]]*\])\s*<\s*(-?\d+(?:\.\d+)?)/g, '$1strictLessThan($2, $3)');
  
  // Pattern 11: Number < simple array  
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*<\s*(\[[^\]]*\])/g, '$1strictLessThan($2, $3)');
  
  // Pattern 11b: Simple object < number
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(\{[^}]*\})\s*<\s*(-?\d+(?:\.\d+)?)/g, '$1strictLessThan($2, $3)');
  
  // Pattern 11c: Number < simple object  
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*<\s*(\{[^}]*\})/g, '$1strictLessThan($2, $3)');
  
  // Pattern 12: Simple function call < number
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))\s*<\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$,()])/g, '$1strictLessThan($2, $3)');
  
  // Pattern 13: Number < simple function call
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])(-?\d+(?:\.\d+)?)\s*<\s*([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictLessThan($2, $3)');
  
  // Step 2: Restore lambda function bodies (after < operator conversion)
  for (let i = 0; i < lambdaBodiesLT.length; i++) {
    const placeholder = `__LAMBDA_BODY_LT_${i}__`;
    processed = processed.replace(placeholder, lambdaBodiesLT[i]);
  }
  
  // Restore the protected <= operators and convert them to strictLessThanOrEqual
  processed = processed.replace(/__LESS_EQUAL__/g, '__TEMP_LESS_EQUAL__');
  
  // Convert <= operators to strictLessThanOrEqual function calls (similar patterns as >= operator)
  
  // Pattern A1: Arithmetic expressions <= number/identifier
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?)/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern A1b: Arithmetic with numbers (field + number, number + field, etc.) <= something
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?)/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?)/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern A2: Number/identifier <= arithmetic expressions
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*[a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\s*[\+\-\*\/]\s*-?\d+(?:\.\d+)?)/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern A3: Parenthesized expressions <= number/identifier
  processed = processed.replace(/(\([^)]+\))\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern A4: Number/identifier <= parenthesized expressions  
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*__TEMP_LESS_EQUAL__\s*(\([^)]+\))/g, '$1strictLessThanOrEqual($2, $3)');
  
  // Pattern A5: Parenthesized expressions <= parenthesized expressions
  processed = processed.replace(/(\([^)]+\))\s*__TEMP_LESS_EQUAL__\s*(\([^)]+\))/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern A6: Simple function call <= number
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$,()])/g, '$1strictLessThanOrEqual($2, $3)');
  
  // Pattern A7: Number <= simple function call
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])(-?\d+(?:\.\d+)?)\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictLessThanOrEqual($2, $3)');
  
  // Pattern A8: Function call <= identifier
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$,()])/g, '$1strictLessThanOrEqual($2, $3)');
  
  // Pattern A9: Identifier <= function call
  processed = processed.replace(/(^|[^a-zA-Z0-9_$,])([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*\([^)]*\))(?![a-zA-Z0-9_$,()])/g, '$1strictLessThanOrEqual($2, $3)');
  
  // Simple patterns
  // Pattern 1: Simple number <= number
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, '$1strictLessThanOrEqual($2, $3)');
  
  // Pattern 2: Simple identifier <= number
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b(?!\()\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z0-9_$])/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern 3: Number <= simple identifier
  processed = processed.replace(/(^|[^a-zA-Z0-9_$])(-?\d+(?:\.\d+)?)\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$])/g, '$1strictLessThanOrEqual($2, $3)');
  
  // Pattern 4: Simple identifier <= simple identifier  
  processed = processed.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*__TEMP_LESS_EQUAL__\s*([a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$])/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern for quoted strings
  processed = processed.replace(/("[-0-9a-zA-Z]*")\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?)/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?)\s*__TEMP_LESS_EQUAL__\s*("[-0-9a-zA-Z]*")/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/("[-0-9a-zA-Z]*")\s*__TEMP_LESS_EQUAL__\s*("[-0-9a-zA-Z]*")/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern for literals (booleans, null)
  processed = processed.replace(/\b(true|false|null)\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())(?![a-zA-Z0-9_$])/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?|[a-zA-Z_$][a-zA-Z0-9_$]*(?!\())\s*__TEMP_LESS_EQUAL__\s*\b(true|false|null)\b/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern for arrays
  processed = processed.replace(/(\[[^\]]*\])\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?|"[^"]*"|'[^']*'|true|false|null|[a-zA-Z_$][a-zA-Z0-9_$]*(?:\([^)]*\))?)/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?|"[^"]*"|'[^']*'|true|false|null|[a-zA-Z_$][a-zA-Z0-9_$]*(?:\([^)]*\))?)\s*__TEMP_LESS_EQUAL__\s*(\[[^\]]*\])/g, 'strictLessThanOrEqual($1, $2)');
  
  // Pattern for objects
  processed = processed.replace(/(\{[^}]*\})\s*__TEMP_LESS_EQUAL__\s*(-?\d+(?:\.\d+)?|"[^"]*"|'[^']*'|true|false|null|[a-zA-Z_$][a-zA-Z0-9_$]*(?:\([^)]*\))?)/g, 'strictLessThanOrEqual($1, $2)');
  processed = processed.replace(/(-?\d+(?:\.\d+)?|"[^"]*"|'[^']*'|true|false|null|[a-zA-Z_$][a-zA-Z0-9_$]*(?:\([^)]*\))?)\s*__TEMP_LESS_EQUAL__\s*(\{[^}]*\})/g, 'strictLessThanOrEqual($1, $2)');
  
  // Restore any remaining << operators
  processed = processed.replace(/__LEFT_SHIFT__/g, '<<');
  
  // Convert numeric property access to bracket notation
  // Examples: chart1.1.points.0.y -> chart1[1].points[0].y
  // This must be done before arrow function conversion to avoid conflicts
  // Only convert when preceded by an identifier or closing bracket/paren
  processed = processed.replace(/([a-zA-Z_$][a-zA-Z0-9_$]*|\]|\))\.(\d+)(?=\.|$|[^a-zA-Z0-9_$])/g, '$1[$2]');
  
  // NOW convert Notion-style arrows to JavaScript arrows (after all operator conversions)
  // Example: "filter(items, item -> item.value > 5)" becomes "filter(items, item => item.value > 5)"
  // Also handle object literal returns by adding parentheses where needed
  processed = processed.replace(/(\([^)]*\)|[a-zA-Z_$][a-zA-Z0-9_$]*)\s*->\s*\{/g, (match, params) => {
    // When arrow function returns an object literal, wrap it in parentheses
    return params + ' => ({';
  });
  
  // Handle the closing brace for object literals
  // This is tricky - we need to find matching closing braces after => ({
  let objectLiteralStart = processed.indexOf(' => ({');
  while (objectLiteralStart !== -1) {
    let braceCount = 1;
    let pos = objectLiteralStart + 6; // Start after ' => ({'
    let inString = false;
    let stringChar = null;
    
    // Find the matching closing brace
    while (pos < processed.length && braceCount > 0) {
      const char = processed[pos];
      
      if (!inString) {
        if (char === '"' || char === "'") {
          inString = true;
          stringChar = char;
        } else if (char === '{') {
          braceCount++;
        } else if (char === '}') {
          braceCount--;
          if (braceCount === 0) {
            // Found the matching closing brace, add closing parenthesis
            processed = processed.substring(0, pos + 1) + ')' + processed.substring(pos + 1);
            break;
          }
        }
      } else if (char === stringChar && processed[pos - 1] !== '\\') {
        inString = false;
      }
      
      pos++;
    }
    
    // Look for the next object literal
    objectLiteralStart = processed.indexOf(' => ({', objectLiteralStart + 1);
  }
  
  // Convert remaining arrows
  processed = processed.replace(/->/g, '=>');
  
  // Restore the protected operators  
  processed = processed.replace(/__TRIPLE_EQUALS__/g, '===');
  processed = processed.replace(/__NOT_TRIPLE_EQUALS__/g, '!==');
  processed = processed.replace(/__TEMP_GREATER_EQUAL__/g, '>=');
  processed = processed.replace(/__TEMP_LESS_EQUAL__/g, '<=');
  
  // CRITICAL: Restore string literals after all replacements
  for (let i = 0; i < stringLiterals.length; i++) {
    const placeholder = `__STRING_LITERAL_${i}__`;
    processed = processed.replace(placeholder, stringLiterals[i]);
  }
  
  return processed;
}

/**
 * Safely evaluates a formula expression
 * 
 * @param {string} expression - Formula expression to evaluate
 * @param {Object} fieldValues - Map of field ID to current value
 * @param {string} formulaId - ID of the formula being evaluated (for error reporting)
 * @returns {any} - Result of the formula evaluation
 * @throws {FormulaEvalError} - When evaluation fails
 */
export function evalFormula(expression, fieldValues, formulaId) {
  // Validate inputs
  if (!expression || typeof expression !== 'string') {
    throw new FormulaEvalError('Formula expression must be a non-empty string', formulaId);
  }
  
  if (!fieldValues || typeof fieldValues !== 'object') {
    throw new FormulaEvalError('Field values must be provided as an object', formulaId);
  }

  try {
    // Preprocess the expression (handle Notion-style syntax)
    const processedExpression = preprocessExpression(expression.trim());
    
    if (!processedExpression) {
      throw new FormulaEvalError('Formula expression is empty after preprocessing', formulaId);
    }
    
    // Create evaluation context where field values take precedence over built-ins
    // This way, if a field is named "count", it will override the built-in count function
    const evaluationContext = { ...builtins, ...fieldValues };
    
    // Instead of passing field IDs as parameter names (which fails for IDs starting with numbers),
    // we'll pass the entire context as a single object and use 'with' statement or destructuring
    // to make the properties available
    const functionBody = `
      "use strict";
      const __context = arguments[0];
      const __getFieldValue = (fieldId) => __context[fieldId];
      
      // Create local variables for all context properties using valid JavaScript identifiers
      ${Object.keys(evaluationContext).map((key, index) => {
        // Only create variables for valid JavaScript identifiers
        if (/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(key)) {
          return `const ${key} = __context[${JSON.stringify(key)}];`;
        }
        // For invalid identifiers (like those starting with numbers), they can only be accessed via __context
        return '';
      }).filter(Boolean).join('\n      ')}
      
      // Evaluate the expression
      return (${processedExpression});
    `;
    
    // Create the function with no parameters (we'll pass context as argument)
    const evalFunction = new Function(functionBody);
    
    // Execute with the evaluation context as the single argument
    const result = evalFunction(evaluationContext);
    
    // Handle special result values
    if (typeof result === 'number' && Number.isNaN(result)) {
      throw new FormulaEvalError('Formula result is NaN (Not a Number)', formulaId);
    }
    
    return result;
    
  } catch (error) {
    // If it's already a FormulaEvalError, re-throw it
    if (error instanceof FormulaEvalError) {
      throw error;
    }
    
    // Wrap other errors to provide context
    const errorMessage = error.message || String(error);
    throw new FormulaEvalError(`${errorMessage}`, formulaId);
  }
}